import React, {useState, useRef, useEffect} from 'react';
import LetterBox from '../components/LetterBox';
import {useSelector, useDispatch} from 'react-redux';
import PropTypes from 'prop-types';
import {enterCollectingMode, enterIdleMode, collectLetter, enterAnsweringMode} from '../actions/actions';
import {COLLECTING_GAME_MODE, IDLE_GAME_MODE, GAME_COMPLETED_MODE, EDITING_GAME_MODE, gap} from '../constants';
import {getWordsState, getDirectionByCoordinates} from '../services/letters-service';
import GameOver from '../components/GameOver';

import './Board.scss';

const propTypes = {
    words: PropTypes.array,
    enterCollectingMode: PropTypes.func,
    enterIdleMode: PropTypes.func,
    enterAnsweringMode: PropTypes.func,
    gameMode: PropTypes.string,
};

const Board = () => {
    const collectedLetters = useSelector((state) => state.collectedLetters);
    const words = useSelector((state) => state.words);
    const gameMode = useSelector((state) => state.gameMode);
    const usedLetters = useSelector((state) => state.usedLetters);
    const [collectingDir, setCollectionDir] = useState(null);
    const board = useRef(null);
    const wordsCount = useRef(0);
    const [flatLettersObjArray, setFlatLettersObjArray] = useState([]);
    const dispatch = useDispatch();
    const [dimensions, setDimensions] = useState({rows: 0, cols: 0});

    /**
     * If the new words count is different then the count we store on the
     * component ref, we would like to calculate the words metrics again
     * and set the state of the flat letter object array
     */
    useEffect(() => {
        if (words.length !== wordsCount.current) {
            wordsCount.current = words.length;
            const {letters, cols, rows} = getWordsState(words);
            setDimensions({rows, cols});
            setFlatLettersObjArray(letters);
        }
    }, [words, wordsCount]);

    function onMouseDown(event) {
        if (gameMode === IDLE_GAME_MODE) {
            const letterBox = event.target;
            if (!letterBox.classList.contains('letter-box')) return;

            dispatch(enterCollectingMode());
            const {xpos, ypos} = letterBox.dataset;
            const letterIndex = flatLettersObjArray.findIndex((item) => {
                return item.xPos === parseInt(xpos) && item.yPos === parseInt(ypos);
            });
            dispatch(collectLetter(flatLettersObjArray[letterIndex]));
        }
    }

    function onMouseUp(event) {
        if (gameMode === COLLECTING_GAME_MODE) {
            let action;
            if (collectedLetters.length > 0) {
                setCollectionDir(null);
                action = enterAnsweringMode();
            } else {
                action = enterIdleMode();
            }
            dispatch(action);
        }
    }

    function onMouseMove(event) {
        if (gameMode === COLLECTING_GAME_MODE) {
            const letterBox = event.target;
            if (!letterBox.classList.contains('letter-box') || letterBox.classList.contains('collected')) return;
            const {xpos, ypos} = letterBox.dataset;

            if (collectedLetters.length) {
                // determine the direction the user is collecting words
                const previousCoordinate = collectedLetters[collectedLetters.length - 1];
                const currentCoordinate = {xPos: parseInt(xpos), yPos: parseInt(ypos)};
                const direction = getDirectionByCoordinates(previousCoordinate, currentCoordinate);

                if (!collectingDir) {
                    setCollectionDir(direction);
                } else if (direction !== collectingDir) {
                    return;
                }
            }

            const letterIndex = flatLettersObjArray.findIndex((item) => {
                return item.xPos === parseInt(xpos) && item.yPos === parseInt(ypos);
            });
            dispatch(collectLetter(flatLettersObjArray[letterIndex]));
        }
    }

    return (
        <div className="board" ref={board} onMouseDown={onMouseDown} onMouseUp={onMouseUp} onMouseMove={onMouseMove}>
            {gameMode === GAME_COMPLETED_MODE ? (
                <GameOver />
            ) : (
                <div
                    className={`letters-container ${gameMode === EDITING_GAME_MODE ? 'edit-mode' : ''}`}
                    style={{width: `${gap * dimensions.cols - 6}px`, height: `${gap * dimensions.rows - 6}px`, top: 0}}
                >
                    {flatLettersObjArray.map((letter) => (
                        <LetterBox
                            key={letter.id}
                            xpos={letter.xPos}
                            ypos={letter.yPos}
                            letter={letter.value}
                            collected={collectedLetters.findIndex((item) => item.id === letter.id) > -1}
                            used={usedLetters.findIndex((id) => id === letter.id) > -1}
                        ></LetterBox>
                    ))}
                </div>
            )}
        </div>
    );
};

Board.propTypes = propTypes;
export default Board;
